/*
 * ScorecardsPanel — per-TP 30-day performance rollup.
 * Shows completion %, cancellation %, on-time %, ML efficiency.
 * Includes the global efficiency-stats widget at the top.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { BarChart3, Clock, MapPin, Truck, AlertTriangle, TrendingUp, TrendingDown, Minus } from "lucide-react";

function pct(v) { return v == null ? "—" : `${v}%`; }
function eff(v) { return v == null ? "—" : v.toFixed(2) + "×"; }

function EfficiencyArrow({ value }) {
  if (value == null) return <Minus className="w-4 h-4 text-slate-400 inline"/>;
  if (value >= 1.05) return <TrendingUp className="w-4 h-4 text-green-600 inline" title="Overestimated (finished faster/shorter than planned)"/>;
  if (value <= 0.95) return <TrendingDown className="w-4 h-4 text-red-600 inline" title="Underestimated (ran long/further than planned)"/>;
  return <Minus className="w-4 h-4 text-slate-400 inline" title="On target"/>;
}

export default function ScorecardsPanel() {
  const [days, setDays] = useState(30);
  const [cards, setCards] = useState([]);
  const [effGlobal, setEffGlobal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [scr, eff] = await Promise.all([
        apiClient.get(`/tpt/scorecards?days=${days}`),
        apiClient.get(`/tpt/efficiency-stats?days=${days}`),
      ]);
      setCards(scr.data.scorecards || []);
      setEffGlobal(eff.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load scorecards");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="scorecards-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-forest"/>
          <div>
            <h2 className="text-xl font-bold text-slate_navy">TP Scorecards</h2>
            <p className="text-sm text-slate-500">Per-provider performance and ML efficiency signals · rolling {days}-day window.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">Window</div>
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-3 py-1 rounded-md text-xs font-semibold border ${days === n ? "bg-forest text-white border-forest" : "border-[#E2E0D8] text-slate-600"}`}
              data-testid={`window-${n}`}
            >{n}d</button>
          ))}
        </div>
      </div>

      {/* Global efficiency widget */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Estimated vs actual duration"
          value={effGlobal ? eff(effGlobal.duration?.avg_efficiency) : "…"}
          detail={effGlobal ? `${effGlobal.duration?.avg_estimated_min ?? "—"} min est · ${effGlobal.duration?.avg_actual_min ?? "—"} min actual` : ""}
          icon={<Clock className="w-6 h-6 text-blue-600"/>}
          arrow={effGlobal?.duration?.avg_efficiency}
          testid="global-eff-duration"
        />
        <StatCard
          label="Estimated vs actual distance"
          value={effGlobal ? eff(effGlobal.distance?.avg_efficiency) : "…"}
          detail={effGlobal ? `${effGlobal.distance?.avg_estimated_mi ?? "—"} mi est · ${effGlobal.distance?.avg_actual_mi ?? "—"} mi actual` : ""}
          icon={<MapPin className="w-6 h-6 text-violet-600"/>}
          arrow={effGlobal?.distance?.avg_efficiency}
          testid="global-eff-distance"
        />
        <StatCard
          label="Sample size"
          value={effGlobal ? String(effGlobal.sample_size ?? 0) : "…"}
          detail={effGlobal?.sample_size === 0 ? "No completed trips yet in this window." : "completed trips analyzed"}
          icon={<Truck className="w-6 h-6 text-slate-500"/>}
          testid="global-eff-samples"
        />
      </div>

      {/* Per-TP table */}
      {loading && <div className="text-center text-slate-500 py-12">Loading scorecards…</div>}
      {!loading && cards.length === 0 && (
        <div className="text-center py-16 text-slate-500 bg-white border border-dashed border-[#E2E0D8] rounded-xl">
          No trip data in the last {days} days.
        </div>
      )}
      {!loading && cards.length > 0 && (
        <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Provider</th>
                <th className="text-right px-3 py-3">Trips</th>
                <th className="text-right px-3 py-3">In progress</th>
                <th className="text-right px-3 py-3">Complete</th>
                <th className="text-right px-3 py-3">Cancel</th>
                <th className="text-right px-3 py-3">No-show</th>
                <th className="text-right px-3 py-3">On-time</th>
                <th className="text-right px-3 py-3">Dead miles</th>
                <th className="text-right px-3 py-3">Dur eff</th>
                <th className="text-right px-3 py-3">Dist eff</th>
                <th className="text-right px-3 py-3">CPD declines</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EFE8]">
              {cards.map((c) => (
                <tr key={c.tp_id} className="hover:bg-slate-50" data-testid={`scorecard-row-${c.tp_id}`}>
                  <td className="px-4 py-3 font-semibold text-slate_navy">{c.tp_name}</td>
                  <td className="text-right px-3 py-3">{c.total_trips}</td>
                  <td className="text-right px-3 py-3 text-slate-500">{c.in_progress}</td>
                  <td className={`text-right px-3 py-3 font-semibold ${c.completion_pct >= 80 ? "text-green-700" : c.completion_pct >= 50 ? "text-slate-700" : "text-slate-400"}`}>{pct(c.completion_pct)}</td>
                  <td className={`text-right px-3 py-3 ${c.cancellation_pct > 10 ? "text-red-600 font-semibold" : "text-slate-500"}`}>{pct(c.cancellation_pct)}</td>
                  <td className={`text-right px-3 py-3 ${c.no_show_pct > 5 ? "text-orange-600 font-semibold" : "text-slate-500"}`}>{pct(c.no_show_pct)}</td>
                  <td className={`text-right px-3 py-3 ${c.on_time_pct == null ? "text-slate-400" : c.on_time_pct >= 90 ? "text-green-700 font-semibold" : c.on_time_pct >= 70 ? "text-slate-700" : "text-red-600 font-semibold"}`}>{pct(c.on_time_pct)}</td>
                  <td className="text-right px-3 py-3 text-slate-600">{c.total_dead_miles.toFixed(1)} mi</td>
                  <td className="text-right px-3 py-3"><EfficiencyArrow value={c.avg_duration_efficiency}/> <span className="text-slate-600">{eff(c.avg_duration_efficiency)}</span></td>
                  <td className="text-right px-3 py-3"><EfficiencyArrow value={c.avg_distance_efficiency}/> <span className="text-slate-600">{eff(c.avg_distance_efficiency)}</span></td>
                  <td
                    className={`text-right px-3 py-3 ${c.cpd_declines > 0 ? "text-orange-600 font-semibold" : "text-slate-400"}`}
                    data-testid={`scorecard-cpd-declines-${c.tp_id}`}
                    title={Object.entries(c.cpd_decline_reasons || {}).map(([r, n]) => `${r.replace(/_/g, " ")}: ${n}`).join(", ") || "No declines"}
                  >
                    {c.cpd_declines || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 text-slate-500 shrink-0"/>
          <div>
            <b>Reading efficiency:</b> a value of 1.00× means our estimate matched actual within seconds/tenths of a mile.
            {" "}<TrendingUp className="w-3 h-3 text-green-600 inline"/> above 1.05 means we overestimated (we can quote shorter times).
            {" "}<TrendingDown className="w-3 h-3 text-red-600 inline"/> below 0.95 means we consistently ran long — adjust estimates upward.
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, detail, icon, arrow, testid }) {
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div>{icon}</div>
      </div>
      <div className="text-3xl font-bold text-slate_navy mb-1">
        {value} {arrow != null && <EfficiencyArrow value={arrow}/>}
      </div>
      <div className="text-xs text-slate-500">{detail}</div>
    </div>
  );
}
